import WarningIcon from '@mui/icons-material/Warning';
import {
  useQueries,
  useQueryClient,
  UseQueryResult,
} from '@tanstack/react-query';
import { lastDayOfMonth } from 'date-fns';
import { ReactNode, useMemo, useState } from 'react';
import {
  Control,
  FieldErrors,
  useForm,
  UseFormGetValues,
  UseFormHandleSubmit,
  UseFormSetError,
  UseFormSetValue,
  useWatch,
} from 'react-hook-form';

import Logger from 'browser-logging/src/browser/Logger';

import { getAllocatedLineItemMappings } from 'common/src/allocations/allocatedLineItemMappings';
import { filterReverseAllocationLineItems } from 'common/src/allocations/filterAllocationLineItems';
import { SingleOperators } from 'common/src/query-params/operators';
import { formatISODateOnly } from 'common/src/transformers/ISODateSerializer';
import {
  ICreateBillDto,
  IUpdateAllocationDto,
  IUpdateAllocationLineItemDto,
  IUpdateAllocationsDto,
} from 'common/src/types/bill.dtos';
import { BillEntryMethod } from 'common/src/types/enums/BillEntryMethod.enum';
import { DemandLineItemTypes } from 'common/src/types/enums/LineItemType.enum';
import { ServiceDemandUnits } from 'common/src/types/enums/Service.enum';
import {
  IAccount,
  IBill,
  ILineItem,
  ILineItemSubtype,
  IMeter,
} from 'common/src/types/interfaces';

import {
  getAccount,
  getAccounts,
  getLastBill,
} from 'portal/src/api/AccountsService';
import { previewAllocation } from 'portal/src/api/BillsService';
import { QUERY_KEYS } from 'portal/src/api/constants';
import { getLineItemSubtypesService } from 'portal/src/api/LineItemSubtypesService';
import { getMeters } from 'portal/src/api/MetersService';
import useAuth from 'portal/src/auth/useAuth';
import ConfirmDialog from 'portal/src/components/common/ConfirmDialog';
import { prepareFormPayload } from 'portal/src/components/common/forms/handleFormData';
import { MESSAGES } from 'portal/src/constants/snackbarMessages';
import useSnackbarHandler from 'portal/src/utils/Snackbar/useSnackbarHandler';
import { ValidationError } from 'portal/src/utils/ValidationError';
import { ValidateGuard } from 'portal/src/validators/validateGuard';

import { defaultValues } from './BillForm.fields';
import { BillFormValidators } from './BillForm.validators';
import { BillFormContext } from './BillFormContext';

import { AllocationLineItemRow } from './Allocations/AllocationLineItemRow';

export interface IBillHeaderFormValues {
  amount: number;
  amountDue?: number;
  balanceForward?: number;
  budgetPeriod?: Date;
  date?: Date;
  dueDate?: Date;
  endDate?: Date;
  estimated: boolean;
  image: string;
  internalNotes: string;
  isPaid: boolean;
  lateFees?: number;
  needsReview: boolean;
  notes: string;
  number: string;
  paidAmount: number;
  paidDate?: Date;
  rateSchedule?: string;
  startDate?: Date;
  workingImage?: File;
}

export interface IBillHeaderForm {
  control: Control<IBillHeaderFormValues, any, IBillHeaderFormValues>;
  formState: {
    dirtyFields: Partial<
      Readonly<{
        amount?: boolean;
        budgetPeriod?: boolean;
        date?: boolean;
        dueDate?: boolean;
        endDate?: boolean;
        estimated?: boolean;
        image?: boolean;
        needsReview?: boolean;
        notes?: boolean;
        number?: boolean;
        startDate?: boolean;
        workingImage?: boolean;
      }>
    >;
    errors: FieldErrors<IBillHeaderFormValues>;
    isDirty: boolean;
    isValid: boolean;
  };
  handleSubmit: UseFormHandleSubmit<IBillHeaderFormValues>;
  getValues: UseFormGetValues<IBillHeaderFormValues>;
  setError: UseFormSetError<IBillHeaderFormValues>;
  setValue: UseFormSetValue<IBillHeaderFormValues>;
}

export interface IBillFormProviderProps {
  children: ReactNode;
  defaultAccountId: string;
  defaultBill?: Partial<IBill>;
}

const higherDecimalSize = (compare: number, value: number) => {
  const compareSize = compare.toString().split('.')[1]?.length ?? 0;
  const valueSize = value.toString().split('.')[1]?.length ?? 0;
  return compareSize > valueSize ? compareSize : valueSize;
};

const calculateLineItemTotal = (lineItems: Partial<ILineItem>[] = []) => {
  return lineItems.reduce((acc = 0, { cost = 0 }) => {
    const higherDecimal = higherDecimalSize(acc, cost);
    acc += Number(cost);
    return parseFloat(acc.toFixed(higherDecimal));
  }, 0);
};

const sortLineItemsBySortIndex = (lineItems: Partial<ILineItem>[] = []) => {
  return lineItems.sort((a, b) => (a.sortIndex || 0) - (b.sortIndex || 0));
};

const BillFormProvider = ({
  children,
  defaultAccountId,
  defaultBill,
}: IBillFormProviderProps) => {
  const { getUser } = useAuth();
  const { organizationId } = getUser();
  const snackbarHandler = useSnackbarHandler();

  const [accountId, setAccountId] = useState(defaultAccountId);
  const [bill, setBill] = useState({ ...defaultBill });
  const [lineItems, setLineItems] = useState(
    defaultBill?.lineItems
      ? sortLineItemsBySortIndex(defaultBill?.lineItems)
      : [],
  );
  const [editableAllocations, setEditableAllocations] = useState(
    bill?.allocatedBills,
  );
  const [hasUnsavedLineItemEdits, setHasUnsavedLineItemEdits] = useState(false);
  const [total, setTotal] = useState(calculateLineItemTotal(lineItems));
  const [dirtyLineItems, setDirtyLineItems] = useState(false);
  const [dirtyAccountId, setDirtyAccountId] = useState(false);
  const [warningDialog, setWarningDialog] = useState(false);
  const [warningMessageDialog, setWarningMessageDialog] = useState('');
  const [warningActionDialog, setWarningActionDialog] = useState<() => void>(
    () => () => {
      return;
    },
  );
  const {
    control,
    formState: { dirtyFields, errors, isDirty, isValid },
    getValues,
    handleSubmit,
    reset,
    setError,
    setValue,
  } = useForm({ defaultValues: defaultValues(bill) });

  const queryClient = useQueryClient();

  const [
    { data: account },
    {
      data: { data: allAccounts },
      isFetching: accountsIsFetching,
      isError: accountsError,
    },
    {
      data: { data: lineItemSubtypes },
      isError: lineItemSubtypesError,
      isFetching: lineItemSubtypesIsFetching,
    },
    {
      data: { data: meters },
      isError: metersError,
      isFetching: metersIsFetching,
    },
    {
      data: previousBill,
      isError: previousBillError,
      isFetching: previousBillIsFetching,
    },
    {
      data: allocationPreview,
      isError: allocationPreviewError,
      isFetching: allocationsPreviewIsFetching,
      refetch: fetchAllocationPreview,
    },
  ] = useQueries<
    [
      UseQueryResult<Partial<IAccount>>,
      UseQueryResult<{ data: Partial<IAccount>[] }>,
      UseQueryResult<{ data: ILineItemSubtype[] }>,
      UseQueryResult<{ data: Partial<IMeter>[] }>,
      UseQueryResult<IBill>,
      UseQueryResult<Partial<IBill>[]>,
    ]
  >({
    queries: [
      {
        queryKey: ['billFormAccount', accountId],
        queryFn: async () => await getAccount(accountId),
        initialData: {} as Partial<IAccount>,
        enabled: !!accountId,
      },
      {
        queryKey: ['billFormAccounts'],
        queryFn: async () => await getAccounts(),
        initialData: { data: [] },
      },

      {
        queryKey: ['billFormLineItemSubtypes'],
        queryFn: async () => await getLineItemSubtypesService(),
        initialData: { data: [] },
      },
      {
        queryKey: [QUERY_KEYS.meters],
        queryFn: async () => {
          return await getMeters({
            filters: [
              {
                field: 'isActive',
                operator: SingleOperators.EQUALS,
                value: true,
              },
            ],
          });
        },
        initialData: { data: [] },
        enabled: !!accountId,
      },
      {
        queryKey: ['billFormPreviousBill', accountId, bill?.id],
        queryFn: async () => {
          return (
            (await getLastBill(
              accountId,
              bill?.id
                ? {
                    excludeBillId: bill.id,
                    maxStartDate: bill.startDate,
                  }
                : {},
            )) ?? {}
          );
        },
        initialData: undefined,
        enabled: !!accountId,
      },
      {
        queryKey: ['billFormAllocationPreview'],
        queryFn: async () => {
          const payload = getAllocationPayload();

          const allocations = await previewAllocation(
            payload as ICreateBillDto,
          );

          // assign temp IDs to the allocation line items
          allocations.forEach((allocation, aIdx) => {
            allocation.lineItems.forEach((li, idx) => {
              li.id = `preview--${aIdx}--${idx}`;
            });
          });

          return allocations;
        },
        initialData: [],
        enabled: false,
      },
    ],
  });

  const [amount, endDate, startDate] = useWatch({
    name: ['amount', 'endDate', 'startDate'],
    control,
  });

  const generateNewBillLineItems = () => {
    // If we don't have line items, this is a new bill so copy the line items from the previous bill
    if (
      (!Array.isArray(lineItems) || !lineItems.length) &&
      amount != null &&
      endDate &&
      previousBill &&
      startDate
    ) {
      const { lineItems: previousBillLineItems = [] } = previousBill ?? {};

      const filteredPreviousItems = filterReverseAllocationLineItems(
        previousBillLineItems,
      );

      // Sort the previous items by sortIndex
      const sortedPreviousItems = sortLineItemsBySortIndex(
        filteredPreviousItems,
      );

      const lineItemsParsed = sortedPreviousItems.map((item) => {
        const hasDemandUnits =
          !!item.meter?.serviceType &&
          !!ServiceDemandUnits[item.meter.serviceType]?.length;
        const shouldResetTypeAndUnit =
          DemandLineItemTypes.includes(item.type) && !hasDemandUnits;

        return {
          cost: filteredPreviousItems.length === 1 ? amount : 0,
          description: item.description,
          subTypeId: item.subType?.id ?? undefined,
          subType: item.subType,
          type: shouldResetTypeAndUnit ? null : item.type,
          meter: item.meter,
          meterId: item.meterId,
          sortIndex: item.sortIndex,
          unitOfMeasure: shouldResetTypeAndUnit ? null : item.unitOfMeasure,
          endDate: formatISODateOnly(endDate),
          startDate: formatISODateOnly(startDate),
        };
      });

      handleSetLineItemsDirty(lineItemsParsed);
    }
  };

  const handleWarnings = (obj) => {
    const { message, validate, next } = obj;
    setWarningDialog(false);

    if (message && validate) {
      const [isValid, validatorObject] = validate();
      const action = () => handleWarnings(validatorObject);

      setWarningMessageDialog(message);
      setWarningActionDialog(() => action);

      if (!isValid) {
        setWarningDialog(true);
      } else {
        action();
      }
    }
    if (next) {
      next();
    }
  };

  const validate = async (onSuccess: () => void): Promise<void> => {
    try {
      const billFormValidator = new BillFormValidators(getValues, lineItems);
      await ValidateGuard.validate(
        billFormValidator.getLineItemsValidator(total),
      );

      handleWarnings(
        billFormValidator.getWarningsValidators(
          hasUnsavedLineItemEdits,
          onSuccess,
        )[0],
      );
    } catch (err) {
      if (err instanceof ValidationError) {
        snackbarHandler.error(err.errors.join('; \n'));
      } else {
        Logger.captureError(err as Error);
        snackbarHandler.error(MESSAGES.GENERIC.ERROR);
      }
    }
  };

  const handleSetBill = (newBill: Partial<IBill>) => {
    setBill(newBill);
    setDirtyLineItems(false);
    setHasUnsavedLineItemEdits(false);
    handleSetLineItems(newBill.lineItems ?? []);
    reset(defaultValues(newBill));
    queryClient.resetQueries({
      queryKey: ['billFormPreviousBill'],
      exact: false,
    });
    queryClient.resetQueries({
      queryKey: ['billFormAllocationPreview'],
      exact: false,
    });
  };

  const handleSetAccountId = (newId: string) => {
    setDirtyAccountId(true);
    setAccountId(newId);
  };

  const handleSetLineItems = (newLineItems: Partial<ILineItem>[]) => {
    setLineItems(newLineItems);
    setTotal(calculateLineItemTotal(newLineItems));
  };

  const handleSetLineItemsDirty = (newLineItems: Partial<ILineItem>[]) => {
    handleSetLineItems(newLineItems);
    setDirtyLineItems(true);
    setHasUnsavedLineItemEdits(false);
  };

  const handleAllocationLineItemEdit = (newLineItem: AllocationLineItemRow) => {
    setEditableAllocations((prevAllocations) => {
      return prevAllocations.map((a) => {
        if (a.id === newLineItem.billId) {
          const lineItems = a.lineItems.map((li) => {
            if (li.id === newLineItem.id) {
              return {
                ...li,
                usage: newLineItem.usage,
                cost: newLineItem.cost,
              };
            }

            return li;
          });

          return {
            ...a,
            lineItems,
          };
        }

        return a;
      });
    });

    return newLineItem;
  };

  const buildLineItemsPayload = (): Partial<ILineItem>[] =>
    lineItems.map((li) => {
      const item: Partial<ILineItem> = {} as any;
      if (li.id) item.id = li.id;
      if (bill.id) item.billId = bill.id;

      item.cost = li.cost != null ? li.cost : 0;

      if (li.endDate)
        item.endDate =
          li.endDate && typeof li.endDate !== 'string'
            ? formatISODateOnly(li.endDate as Date)
            : li.endDate;
      if (li.startDate)
        item.startDate =
          li.startDate && typeof li.startDate !== 'string'
            ? formatISODateOnly(li.startDate as Date)
            : li.startDate;

      item.usage = li.usage != null ? li.usage : 0;

      if (li.sortIndex || li.sortIndex === 0) item.sortIndex = li.sortIndex;

      if (li.description) item.description = li.description;
      if (li.type) item.type = li.type;
      item.subTypeId = li.subTypeId || null;
      if (li.meterId) item.meterId = li.meterId;
      item.meterCurrentReading = li.meterCurrentReading;
      item.meterPreviousReading = li.meterPreviousReading;
      // the meter reading multiplier can have a default value from the meter, do not save the value unless we have readings
      item.meterReadingMultiplier =
        li.meterCurrentReading != null && li.meterPreviousReading != null
          ? li.meterReadingMultiplier
          : null;
      if (li.unitOfMeasure) item.unitOfMeasure = li.unitOfMeasure;

      return item;
    });

  const getBillPayload = (): Partial<IBill> => {
    const rawValues = getValues();
    const { workingImage: _workingImage, ...formValues } = rawValues;
    const payload = prepareFormPayload(
      formValues,
      dirtyFields,
    ) as unknown as Partial<IBill>;

    if ('date' in payload)
      payload.date = rawValues.date
        ? formatISODateOnly(rawValues.date)
        : undefined;
    if ('budgetPeriod' in payload)
      payload.budgetPeriod = rawValues.budgetPeriod
        ? formatISODateOnly(lastDayOfMonth(new Date(rawValues.budgetPeriod)))
        : undefined;
    if ('dueDate' in payload)
      payload.dueDate = rawValues.dueDate
        ? formatISODateOnly(rawValues.dueDate)
        : undefined;
    if ('endDate' in payload)
      payload.endDate = rawValues.endDate
        ? formatISODateOnly(rawValues.endDate)
        : undefined;
    if ('paidDate' in payload)
      payload.paidDate = rawValues.paidDate
        ? formatISODateOnly(rawValues.paidDate)
        : undefined;
    if ('startDate' in payload)
      payload.startDate = rawValues.startDate
        ? formatISODateOnly(rawValues.startDate)
        : undefined;

    if (dirtyLineItems) payload.lineItems = buildLineItemsPayload();

    if (dirtyAccountId) payload.accountId = accountId;

    // if this is a new bill, add required fields
    // amount is required but defaults to 0, so it may not be marked dirty
    if (!bill.id) {
      payload.amount = rawValues.amount;
      payload.billEntryMethod = BillEntryMethod.Manual;
      payload.organizationId = organizationId;
    }

    return payload;
  };

  const getAllocationPayload = () => {
    const rawValues = getValues();
    const payload = {
      accountId,
      amount: rawValues.amount,
      billEntryMethod: bill?.billEntryMethod ?? BillEntryMethod.Manual,
      endDate: rawValues.endDate
        ? formatISODateOnly(rawValues.endDate)
        : undefined,
      lineItems: buildLineItemsPayload(),
      organizationId,
      startDate: rawValues.startDate
        ? formatISODateOnly(rawValues.startDate)
        : undefined,
    };

    // assign temp IDs to the line items so that we can group allocated line items by the master line item
    payload.lineItems?.forEach((li, idx) => {
      if (!li.id) {
        li.id = `preview--${idx}`;
      }
    });

    return payload;
  };

  const getUpdateAllocationsPayload = (): IUpdateAllocationsDto => {
    return {
      allocations: editableAllocations?.map(
        (a) =>
          ({
            id: a.id,
            lineItems: a.lineItems.map(
              (li) =>
                ({
                  id: li.id,
                  cost: li.cost,
                  usage: li.usage,
                }) as IUpdateAllocationLineItemDto,
            ),
          }) as IUpdateAllocationDto,
      ),
    };
  };

  const accountMeters = meters?.filter((m) => m.parentAccountId === accountId);
  const allocationMeters = meters?.filter(
    (m) =>
      m.parentAccountId === accountId ||
      m.masterMeter?.parentAccountId === accountId,
  );
  const allocations = editableAllocations ?? allocationPreview;

  let mappingError = false;
  let mappings = [];
  try {
    mappings = getAllocatedLineItemMappings(
      getAllocationPayload(),
      allocations,
      allocationMeters,
    );
  } catch (err) {
    Logger.captureError(err as Error);
    mappingError = true;
  }

  return (
    <BillFormContext.Provider
      value={{
        account,
        accountId,
        accountMeters: {
          data: accountMeters,
          isError: metersError,
          isFetching: metersIsFetching,
        },
        accounts: {
          data: allAccounts?.filter((a) => !a.isSub),
          isError: accountsError,
          isFetching: accountsIsFetching,
        },
        allocatedLineItemMappings: {
          data: mappings,
          isError: mappingError || metersError || allocationPreviewError,
          isFetching: metersIsFetching || allocationsPreviewIsFetching,
        },
        allocationMeters: {
          data: allocationMeters,
          isError: metersError,
          isFetching: metersIsFetching,
        },
        allocations,
        activeAllocationTemplate: account?.activeAllocationTemplate,
        bill,
        billIsUsedInAllocations:
          // disable editing master bills and allocations
          (bill.isAllocated && account.isMaster) || bill.masterBillId != null,
        fetchAllocationPreview,
        generateNewBillLineItems,
        getBillPayload,
        getUpdateAllocationsPayload,
        onLineItemEditStateChange: setHasUnsavedLineItemEdits,
        headerForm: {
          control,
          formState: { dirtyFields, errors, isDirty, isValid },
          handleSubmit,
          getValues,
          setError,
          setValue,
        },
        lineItems,
        lineItemSubtypes: {
          data: lineItemSubtypes,
          isError: lineItemSubtypesError,
          isFetching: lineItemSubtypesIsFetching,
        },
        previousBill: {
          data: previousBill,
          isError: previousBillError,
          isFetching: previousBillIsFetching,
        },
        processAllocationLineItemEdit: handleAllocationLineItemEdit,
        resetAllocationLineItems: () =>
          setEditableAllocations(bill?.allocatedBills),
        setAccountId: handleSetAccountId,
        setBill: handleSetBill,
        setLineItems: handleSetLineItemsDirty,
        subAccounts: allAccounts?.filter((a) => a.isSub),
        total,
        validate,
      }}
    >
      {children}
      <ConfirmDialog
        open={warningDialog}
        onClose={() => setWarningDialog(false)}
        message={warningMessageDialog}
        primaryButtonLabel={bill.id ? 'Update' : 'Save'}
        onPrimaryButtonClicked={warningActionDialog}
        secondaryButtonLabel="Edit"
        onSecondaryButtonClicked={() => setWarningDialog(false)}
        renderElement={<WarningIcon sx={{ color: 'warning.main' }} />}
      />
    </BillFormContext.Provider>
  );
};

export default BillFormProvider;
